import { ToolDefinition } from "../types";
import { useMemoryStore, MEMORY_LIMITS } from "../memories/memoryStore";

/**
 * Memory tools. Memories are durable, user-scoped notes that Kate can write
 * during a chat and are auto-injected into every subsequent system prompt.
 *
 * Design intent:
 *   - `add_memory`: Kate calls this when the USER explicitly says "remember
 *     X" or after a clarification that should persist (e.g. "always exclude
 *     install runs from customer counts"). She should NOT write speculative
 *     memories — only things the user has confirmed.
 *   - `list_memories`: Mostly diagnostic. The full list is already in the
 *     system prompt, so Kate rarely needs this.
 *   - `delete_memory`: When the user says "forget X", Kate looks up the id
 *     from the system-prompt memory list and deletes by id.
 *
 * The user can also manage memories directly via the MemoriesDialog UI.
 */

export const memoryToolDefinitions: ToolDefinition[] = [
  {
    name: "add_memory",
    description:
      "Save a durable note about the user's preferences, definitions, or " +
      "default filters. Use this ONLY when the user has explicitly asked " +
      "you to remember something, OR after a clarifying answer that should " +
      "obviously persist for future conversations (e.g. 'I always want to " +
      "exclude install runs', 'we call customer ACME by their nickname'). " +
      "Do NOT speculatively save inferences — be conservative.\n\n" +
      "Memories are injected into every future system prompt. Keep titles " +
      `short (<${MEMORY_LIMITS.MAX_TITLE_LENGTH} chars) and content tight.`,
    input_schema: {
      type: "object",
      properties: {
        title: {
          type: "string",
          description:
            "A short label, e.g. 'Default time window' or 'Customer ACME naming'.",
        },
        content: {
          type: "string",
          description:
            "The actual memory text. Be specific and self-contained — " +
            "future you will read this without conversation context.",
        },
      },
      required: ["title", "content"],
    },
  },
  {
    name: "list_memories",
    description:
      "Return all currently saved memories. The system prompt already " +
      "contains them, so you usually do not need this. Use it only if the " +
      "user explicitly asks 'what do you remember about me?' or similar.",
    input_schema: {
      type: "object",
      properties: {},
    },
  },
  {
    name: "delete_memory",
    description:
      "Delete a memory by id. Use the ids exactly as they appear in the " +
      "system-prompt memory list. The user can also delete via the " +
      "Memories dialog UI.",
    input_schema: {
      type: "object",
      properties: {
        id: {
          type: "string",
          description: "The memory id to delete.",
        },
      },
      required: ["id"],
    },
  },
];

export async function executeAddMemory(
  input: Record<string, unknown>,
): Promise<unknown> {
  const title = typeof input.title === "string" ? input.title : "";
  const content = typeof input.content === "string" ? input.content : "";
  const result = useMemoryStore.getState().add({ title, content });
  if ("error" in result) return { success: false, error: result.error };
  return {
    success: true,
    memory: {
      id: result.id,
      title: result.title,
      content: result.content,
    },
  };
}

export async function executeListMemories(): Promise<unknown> {
  const memories = useMemoryStore.getState().memories;
  return {
    count: memories.length,
    memories: memories.map((m) => ({
      id: m.id,
      title: m.title,
      content: m.content,
    })),
  };
}

export async function executeDeleteMemory(
  input: Record<string, unknown>,
): Promise<unknown> {
  const id = typeof input.id === "string" ? input.id : "";
  if (!id) return { success: false, error: "id is required" };
  const ok = useMemoryStore.getState().remove(id);
  return ok ? { success: true } : { success: false, error: "memory not found" };
}
